/**
 * Blocking cache refresh dialog for real workspace recovery.
 */
import { ConfirmModal } from '@affine/component';
import { useI18n } from '@affine/i18n';

import type { WorkspaceRecoveryState } from './use-workspace-recovery';
import * as styles from './workspace-sync-dialog.css';

export interface WorkspaceSyncDialogProps {
  recovery: WorkspaceRecoveryState;
}

export function SyncProgressBar({ progress }: { progress?: number | null }) {
  const value =
    progress === undefined || progress === null
      ? undefined
      : Math.round(Math.min(Math.max(progress, 0), 1) * 100);

  return (
    <div
      className={styles.progressTrack}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
    >
      <div
        className={
          value === undefined
            ? styles.progressFillIndeterminate
            : styles.progressFill
        }
        style={value === undefined ? undefined : { width: `${value}%` }}
      />
    </div>
  );
}

export function WorkspaceSyncDialog({ recovery }: WorkspaceSyncDialogProps) {
  const t = useI18n();
  const { isRecovering, phase, progressPercent, failure, retry } = recovery;

  if (!isRecovering) {
    return null;
  }

  const phaseLabel =
    phase === 'clearingLocalData'
      ? t.t('com.streamlain.workspace.cacheRefresh.clearingLocalData')
      : phase === 'syncingFreshData'
        ? t.t('com.streamlain.workspace.cacheRefresh.syncingFreshData')
        : phase === 'reloading'
          ? t.t('com.streamlain.workspace.cacheRefresh.reloading')
          : t.t('com.streamlain.workspace.cacheRefresh.preparing');

  return (
    <ConfirmModal
      open={true}
      onOpenChange={() => {
        // Cannot close — persistent modal
      }}
      persistent={true}
      withoutCloseButton={true}
      title={t.t('com.streamlain.workspace.cacheRefresh.title')}
      description={
        failure?.kind === 'stillStale'
          ? t.t('com.streamlain.workspace.cacheRefresh.stillStale')
          : failure
            ? t.t('com.streamlain.workspace.cacheRefresh.failed', {
                error: failure.message,
              })
            : t.t('com.streamlain.workspace.cacheRefresh.description')
      }
      confirmText={
        failure ? t.t('com.streamlain.workspace.cacheRefresh.retry') : undefined
      }
      onConfirm={failure ? retry : undefined}
      confirmButtonOptions={
        failure ? { variant: 'primary' } : { style: { display: 'none' } }
      }
      cancelButtonOptions={{ style: { display: 'none' } }}
      onCancel={false}
    >
      {failure ? null : (
        <div className={styles.content}>
          <SyncProgressBar progress={progressPercent} />
          <div className={styles.progressMeta}>{phaseLabel}</div>
        </div>
      )}
    </ConfirmModal>
  );
}
